import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import { subDays, endOfDay, startOfDay } from 'date-fns';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PondMultiSelect } from '@/features/shared/components/trends/PondMultiSelect';
import { ViewToggle } from '@/features/shared/components/trends/ViewToggle';
import { TrendChart } from '@/features/shared/components/trends/TrendChart';
import { DateRangeFilter } from '@/features/shared/components/DateRangeFilter';
import type { ViewMode } from '@/features/shared/components/trends/ViewToggle';
import type { TrendSeries } from '@/features/shared/components/trends/TrendChart';
import type { DateRange } from '@/features/shared/components/DateRangeFilter';
import { maintenanceApi } from '@/api/maintenanceApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/Card';
import { SectionLoading } from '@/shared/components/ui/LoadingSpinner';
import { MetricBox } from '@/features/shared/components/records/MetricBox';
import type { PondResponse } from '@/shared/types/api';
import { Leaf, Bird, Fish, AlertTriangle } from 'lucide-react';

type MaintenanceFilter = 'equipment_issues' | 'algal_bloom' | 'birds' | 'stressed_fish';

interface MaintenanceTrendsTabProps {
  farmId: string;
  ponds: PondResponse[];
}

type MaintenanceMetric = 'water_level' | 'discharge_ph' | 'discharge_do' | 'discharge_flow_rate';

const METRICS: Array<{ key: MaintenanceMetric; label: string; unit: string }> = [
  { key: 'water_level', label: 'Water Level', unit: 'm' },
  { key: 'discharge_ph', label: 'Discharge pH', unit: '' },
  { key: 'discharge_do', label: 'Discharge DO', unit: 'mg/L' },
  { key: 'discharge_flow_rate', label: 'Flow Rate', unit: 'L/min' },
];

const POND_COLORS = ['#10B981', '#3B82F6', '#F59E0B']; // Emerald, Blue, Amber

export const MaintenanceTrendsTab: React.FC<MaintenanceTrendsTabProps> = ({
  farmId,
  ponds,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL query params
  const urlPondIds = searchParams.get('ponds')?.split(',').filter(Boolean) || [];
  const urlMetrics = searchParams.get('metrics')?.split(',').filter(Boolean) as MaintenanceMetric[] || ['water_level'];
  const urlViewMode = (searchParams.get('view') || 'overlay') as ViewMode;
  const urlStartDate = searchParams.get('startDate')
    ? new Date(searchParams.get('startDate')!)
    : startOfDay(subDays(new Date(), 30));
  const urlEndDate = searchParams.get('endDate')
    ? new Date(searchParams.get('endDate')!)
    : endOfDay(new Date());

  const [selectedPondIds, setSelectedPondIds] = useState<string[]>(urlPondIds);
  const [selectedMetrics, setSelectedMetrics] = useState<MaintenanceMetric[]>(urlMetrics);
  const [viewMode, setViewMode] = useState<ViewMode>(urlViewMode);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: urlStartDate,
    endDate: urlEndDate,
  });

  // Sync all filter state to URL while preserving tab and pondId params
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Preserve tab param
    newParams.set('tab', 'trends');

    // Preserve pondId param (for when user switches back to Records tab)
    const existingPondId = searchParams.get('pondId');
    if (existingPondId) {
      newParams.set('pondId', existingPondId);
    }

    // Set pond IDs for trends multi-select
    if (selectedPondIds.length > 0) {
      newParams.set('ponds', selectedPondIds.join(','));
    }

    // Set metrics
    if (selectedMetrics.length > 0) {
      newParams.set('metrics', selectedMetrics.join(','));
    }

    // Set view mode
    newParams.set('view', viewMode);

    // Set date range
    newParams.set('startDate', dateRange.startDate.toISOString());
    newParams.set('endDate', dateRange.endDate.toISOString());

    setSearchParams(newParams, { replace: true });
  }, [selectedPondIds, selectedMetrics, viewMode, dateRange, searchParams, setSearchParams]);

  // Calculate days difference for API calls
  const days = useMemo(() => {
    const diff = Math.ceil((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(diff, 1);
  }, [dateRange]);

  // Fetch trend data for each selected pond in parallel
  const trendQueries = useQueries({
    queries: selectedPondIds.map((pondId) => ({
      queryKey: ['maintenance', 'trends', farmId, pondId, selectedMetrics, days],
      queryFn: async () => {
        const results = await Promise.all(
          selectedMetrics.map((metric) =>
            maintenanceApi.getTrends(farmId, {
              parameter: metric,
              days,
              pondId,
            })
          )
        );
        return results.map((res, idx) => ({ metric: selectedMetrics[idx], data: res.data }));
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  // Fetch statistics for selected ponds
  const statsQuery = useQueries({
    queries: [{
      queryKey: ['maintenance', 'stats', farmId, selectedPondIds],
      queryFn: () => maintenanceApi.getStatistics(farmId, {
        pondIds: selectedPondIds.length > 0 ? selectedPondIds : undefined,
      }),
      enabled: true,
      staleTime: 5 * 60 * 1000,
    }],
  })[0];

  const isLoading = trendQueries.some((q) => q.isLoading) || statsQuery.isLoading;
  const hasError = trendQueries.some((q) => q.isError);

  // Transform data into series format for each metric
  const metricSeries = useMemo(() => {
    const seriesByMetric = new Map<MaintenanceMetric, TrendSeries[]>();

    selectedMetrics.forEach((metric) => {
      const series: TrendSeries[] = [];

      selectedPondIds.forEach((pondId, index) => {
        const query = trendQueries[index];
        const pond = ponds.find((p) => p.id === pondId);

        if (query.data && pond) {
          const metricData = query.data.find((d) => d.metric === metric);
          if (metricData) {
            const data = metricData.data.map((point) => ({
              timestamp: point.date,
              value: point.value,
              pondId,
              pondName: pond.name,
            }));

            series.push({
              pondId,
              pondName: pond.name,
              data,
              color: POND_COLORS[index % POND_COLORS.length],
            });
          }
        }
      });

      seriesByMetric.set(metric, series);
    });

    return seriesByMetric;
  }, [selectedPondIds, selectedMetrics, trendQueries, ponds]);

  const handleMetricToggle = (metricKey: MaintenanceMetric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metricKey)
        ? prev.filter((m) => m !== metricKey)
        : [...prev, metricKey]
    );
  };

  const handleZoomSelect = (range: DateRange) => {
    setDateRange({
      startDate: startOfDay(range.startDate),
      endDate: endOfDay(range.endDate),
    });
  };

  const pondOptions = ponds.map((p) => ({ id: p.id, name: p.name }));
  const stats = statsQuery.data;

  // Navigate to records tab with filter applied
  const navigateToFilteredRecords = useCallback((filter: MaintenanceFilter) => {
    const params = new URLSearchParams();
    params.set('tab', 'records');
    params.set('filter', filter);
    params.set('startDate', dateRange.startDate.toISOString());
    params.set('endDate', dateRange.endDate.toISOString());

    // Preserve pond selection if only one pond is selected
    if (selectedPondIds.length === 1) {
      params.set('pondId', selectedPondIds[0]);
    }

    navigate(`/farms/${farmId}/maintenance?${params.toString()}`);
  }, [farmId, dateRange, selectedPondIds, navigate]);

  return (
    <div className="space-y-6">
      {/* Statistics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricBox
          label="Total Checks"
          value={stats?.totalChecks?.toString() ?? '0'}
          color="slate"
        />
        <MetricBox
          label="This Week"
          value={stats?.checksThisWeek?.toString() ?? '0'}
          color="blue"
        />
        <MetricBox
          label="Avg Water Level"
          value={stats?.avgWaterLevel ? `${stats.avgWaterLevel.toFixed(2)}m` : 'N/A'}
          color="blue"
        />
        <div
          className={`cursor-pointer transition-transform hover:scale-[1.02] ${stats?.equipmentIssueCount && stats.equipmentIssueCount > 0 ? 'hover:ring-2 hover:ring-red-300 rounded-lg' : ''}`}
          onClick={() => stats?.equipmentIssueCount && stats.equipmentIssueCount > 0 && navigateToFilteredRecords('equipment_issues')}
          title={stats?.equipmentIssueCount && stats.equipmentIssueCount > 0 ? 'Click to view equipment issues' : undefined}
        >
          <MetricBox
            label="Equipment Issues"
            value={stats?.equipmentIssueCount?.toString() ?? '0'}
            color={stats?.equipmentIssueCount && stats.equipmentIssueCount > 0 ? 'red' : 'emerald'}
          />
        </div>
      </div>

      {/* Environmental Issues Summary */}
      {stats?.environmentalIssuesByType && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Environmental Issues Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                className={`flex items-center gap-3 p-4 bg-amber-50/50 rounded-lg border border-amber-200/50 transition-all ${
                  (stats.environmentalIssuesByType.algalBloom ?? 0) > 0
                    ? 'cursor-pointer hover:scale-[1.02] hover:ring-2 hover:ring-amber-300'
                    : ''
                }`}
                onClick={() => (stats.environmentalIssuesByType.algalBloom ?? 0) > 0 && navigateToFilteredRecords('algal_bloom')}
                title={(stats.environmentalIssuesByType.algalBloom ?? 0) > 0 ? 'Click to view algal bloom records' : undefined}
              >
                <Leaf className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="text-sm text-slate-600">Algal Blooms</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.environmentalIssuesByType.algalBloom ?? 0}
                  </p>
                </div>
              </div>
              <div
                className={`flex items-center gap-3 p-4 bg-amber-50/50 rounded-lg border border-amber-200/50 transition-all ${
                  (stats.environmentalIssuesByType.presenceOfBirds ?? 0) > 0
                    ? 'cursor-pointer hover:scale-[1.02] hover:ring-2 hover:ring-amber-300'
                    : ''
                }`}
                onClick={() => (stats.environmentalIssuesByType.presenceOfBirds ?? 0) > 0 && navigateToFilteredRecords('birds')}
                title={(stats.environmentalIssuesByType.presenceOfBirds ?? 0) > 0 ? 'Click to view bird presence records' : undefined}
              >
                <Bird className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="text-sm text-slate-600">Bird Presence</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.environmentalIssuesByType.presenceOfBirds ?? 0}
                  </p>
                </div>
              </div>
              <div
                className={`flex items-center gap-3 p-4 bg-red-50/50 rounded-lg border border-red-200/50 transition-all ${
                  (stats.environmentalIssuesByType.stressedFish ?? 0) > 0
                    ? 'cursor-pointer hover:scale-[1.02] hover:ring-2 hover:ring-red-300'
                    : ''
                }`}
                onClick={() => (stats.environmentalIssuesByType.stressedFish ?? 0) > 0 && navigateToFilteredRecords('stressed_fish')}
                title={(stats.environmentalIssuesByType.stressedFish ?? 0) > 0 ? 'Click to view stressed fish records' : undefined}
              >
                <Fish className="h-6 w-6 text-red-600" />
                <div>
                  <p className="text-sm text-slate-600">Stressed Fish</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.environmentalIssuesByType.stressedFish ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <Card>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pond Selection */}
            <PondMultiSelect
              ponds={pondOptions}
              selectedPondIds={selectedPondIds}
              onChange={setSelectedPondIds}
              maxSelection={3}
              label="Select Ponds to Compare"
            />

            {/* Date Range */}
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
              maxDays={90}
              label="Date Range"
            />
          </div>

          {/* Metric Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Metrics
            </label>
            <div className="flex flex-wrap gap-2">
              {METRICS.map((metric) => (
                <button
                  key={metric.key}
                  onClick={() => handleMetricToggle(metric.key)}
                  className={`
                    px-4 py-2 rounded-lg border text-sm font-medium transition-all
                    ${
                      selectedMetrics.includes(metric.key)
                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                        : 'bg-white text-gray-700 border-slate-200/50 hover:border-primary-300 hover:bg-gray-50'
                    }
                  `}
                >
                  {metric.label}
                </button>
              ))}
            </div>
            {selectedMetrics.length === 0 && (
              <p className="mt-2 text-xs text-amber-600">
                Please select at least one metric to display
              </p>
            )}
          </div>

          {/* View Toggle */}
          {selectedPondIds.length > 1 && selectedMetrics.length > 0 && (
            <ViewToggle viewMode={viewMode} onChange={setViewMode} />
          )}
        </div>
      </Card>

      {/* Charts */}
      {selectedPondIds.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200/50 mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              Select ponds to view trends
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              Choose up to 3 ponds and metrics to compare maintenance trends over time.
            </p>
          </div>
        </Card>
      ) : selectedMetrics.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200/50 mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              Select metrics to display
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              Choose at least one metric to view trends for the selected ponds.
            </p>
          </div>
        </Card>
      ) : isLoading ? (
        <SectionLoading message="Loading trend data..." />
      ) : hasError ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 border border-red-200/50 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="font-roboto text-sm font-semibold text-gray-900">
              Failed to load trend data
            </h3>
            <p className="font-roboto mt-1 text-sm text-gray-600">
              There was an error loading the trend data. Please try again.
            </p>
          </div>
        </Card>
      ) : viewMode === 'overlay' ? (
        // Overlay mode: All ponds on one chart per metric
        <div className="space-y-6">
          {selectedMetrics.map((metricKey) => {
            const metric = METRICS.find((m) => m.key === metricKey);
            const series = metricSeries.get(metricKey) || [];
            return (
              <TrendChart
                key={metricKey}
                series={series}
                metricLabel={metric?.label || metricKey}
                metricUnit={metric?.unit}
                height={350}
                onZoomSelect={handleZoomSelect}
              />
            );
          })}
        </div>
      ) : (
        // Separate mode: Each pond gets its own chart for each metric
        <div className="space-y-6">
          {selectedPondIds.map((pondId, pondIndex) => {
            const pond = ponds.find((p) => p.id === pondId);
            if (!pond) return null;

            return (
              <div key={pondId} className="space-y-4">
                <h3 className="font-roboto text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: POND_COLORS[pondIndex % POND_COLORS.length] }}
                  />
                  {pond.name}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {selectedMetrics.map((metricKey) => {
                    const metric = METRICS.find((m) => m.key === metricKey);
                    const allSeries = metricSeries.get(metricKey) || [];
                    const pondSeries = allSeries.filter((s) => s.pondId === pondId);

                    return (
                      <TrendChart
                        key={`${pondId}-${metricKey}`}
                        series={pondSeries}
                        metricLabel={metric?.label || metricKey}
                        metricUnit={metric?.unit}
                        height={250}
                        onZoomSelect={handleZoomSelect}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Equipment Issues Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Equipment Issues Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {statsQuery.isLoading ? (
            <div className="text-center py-8 text-slate-500">
              Loading equipment data...
            </div>
          ) : !stats?.equipmentIssueCount || stats.equipmentIssueCount === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 border border-emerald-200/50 mb-4">
                <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-roboto text-sm font-semibold text-gray-900">
                No equipment issues
              </h3>
              <p className="font-roboto mt-1 text-sm text-gray-600">
                All equipment is functioning normally in the selected time period.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className="flex items-center justify-between p-4 bg-red-50/50 rounded-lg border border-red-200/50 cursor-pointer transition-all hover:scale-[1.01] hover:ring-2 hover:ring-red-300"
                onClick={() => navigateToFilteredRecords('equipment_issues')}
                title="Click to view equipment issues"
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Equipment Issues Detected</p>
                    <p className="text-sm text-slate-600">
                      {stats.equipmentIssueCount} issue(s) found during maintenance checks
                    </p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-red-600">
                  {stats.equipmentIssueCount}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
